import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShoppingBag, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAITransition } from "@/components/AITransition";
import api from "@/lib/api";
import { addToCart, getCart } from "@/lib/cart";

const PINCODE_KEY = "celebrai_pincode";

export default function Shop() {
  const [tabs, setTabs] = useState([]);              // admin-managed categories
  const [items, setItems] = useState([]);
  const [params] = useSearchParams();
  const initialCat = params.get("category");
  const [category, setCategory] = useState(initialCat || "");
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(getCart().length);
  const playAI = useAITransition();
  const [pincode, setPincode] = useState(() => localStorage.getItem(PINCODE_KEY) || "");
  const navigate = useNavigate();

  // Load admin-managed categories dynamically — no hardcoded list.
  useEffect(() => {
    api.get("/categories")
      .then((r) => {
        const list = r.data || [];
        setTabs(list);
        setCategory((prev) => {
          if (prev && list.some((c) => c.slug === prev)) return prev;
          return list[0]?.slug || "";
        });
      })
      .catch(() => setTabs([]));
  }, []);

  useEffect(() => {
    if (!category) { setItems([]); setLoading(false); return; }
    setLoading(true);
    const qs = new URLSearchParams({ category });
    if (/^\d{6}$/.test(pincode)) qs.set("pincode", pincode);
    api.get(`/products?${qs.toString()}`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [category, pincode]);

  const savePincode = () => {
    if (!/^\d{6}$/.test(pincode)) { toast.error("Enter a valid 6-digit pincode"); return; }
    localStorage.setItem(PINCODE_KEY, pincode);
    toast.success("Showing products available for your area");
  };

  useEffect(() => {
    const h = () => setCartCount(getCart().length);
    window.addEventListener("cart-change", h);
    return () => window.removeEventListener("cart-change", h);
  }, []);

  const add = (p) => {
    addToCart({
      product_id: p.id,
      name: p.name,
      category: p.category,
      price: p.price,
      quantity: 1,
      image_url: p.image_url || (p.images && p.images[0]) || "",
      is_global: !!p.is_global,
      delivery_pincode: p.delivery_pincode != null ? String(p.delivery_pincode).trim() : null,
    });
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div className="space-y-8" data-testid="shop-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl text-charcoal">Shop</h1>
          <p className="text-charcoal/60 mt-2">Curated across Jaipur — flowers, cakes, decor &amp; hampers.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={(e) => playAI(() => navigate("/planner"), e)}
            data-testid="shop-ai-cta"
            className="inline-flex items-center gap-2 rounded-full border-2 border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white px-4 py-2 text-sm font-bold transition-colors">
            <Sparkles className="w-4 h-4" strokeWidth={2.25} /> Help me choose
          </button>
          <button onClick={() => navigate("/checkout")} className="pill-btn pill-btn-primary flex items-center gap-2 relative" data-testid="cart-btn">
            <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
            Cart {cartCount > 0 && <span className="ml-1 bg-white text-terracotta rounded-full text-xs font-bold w-5 h-5 flex items-center justify-center">{cartCount}</span>}
          </button>
        </div>
      </div>

      {/* Pincode filter — products are filtered by availability once set. */}
      <div className="card-surface p-4 flex items-center gap-3 flex-wrap" data-testid="shop-pincode-bar">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60">Delivery pincode</span>
        <input type="text" inputMode="numeric" maxLength={6}
          value={pincode}
          onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="6-digit"
          className="w-32 rounded-xl bg-white border border-warm px-3 py-2 focus:outline-none focus:border-terracotta text-sm font-mono"
          data-testid="shop-pincode-input"
        />
        <button type="button" onClick={savePincode}
          className="rounded-full px-4 py-2 text-xs font-bold bg-charcoal text-white"
          data-testid="shop-pincode-apply">
          Apply
        </button>
        {!/^\d{6}$/.test(pincode) && (
          <span className="text-xs text-charcoal/50">Add your pincode to see only what&apos;s available in your area.</span>
        )}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="shop-tabs">
        {tabs.length === 0 ? (
          <p className="text-sm text-charcoal/50">No categories yet.</p>
        ) : (
          tabs.map((t) => (
            <button key={t.slug} onClick={() => setCategory(t.slug)} data-testid={`tab-${t.slug}`}
              className={`pill-btn text-sm whitespace-nowrap ${category === t.slug ? "pill-btn-primary" : "pill-btn-secondary"}`}>
              {t.name}
            </button>
          ))
        )}
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(4)].map((_, i) => <div key={i} className="card-surface h-80 animate-pulse bg-linen-2" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="card-surface p-10 text-center text-charcoal/60" data-testid="shop-empty">No products in this category yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="card-surface overflow-hidden group" data-testid={`product-card-${i}`}>
              <button
                type="button"
                onClick={() => navigate(`/product/${p.id}`)}
                className="block w-full text-left"
                data-testid={`product-open-${i}`}
              >
                <div className="aspect-square overflow-hidden relative">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                  <div className={`absolute top-3 left-3 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-bold cat-${p.category}`}>{p.category}</div>
                </div>
                <div className="p-5 pb-3">
                  <p className="font-display text-lg text-charcoal">{p.name}</p>
                  <p className="text-sm text-charcoal/70 mt-1 line-clamp-2">{p.description}</p>
                </div>
              </button>
              <div className="px-5 pb-5 flex items-center justify-between">
                <span className="font-display text-2xl text-charcoal">₹{p.price}</span>
                <button onClick={() => add(p)} className="pill-btn pill-btn-primary text-sm" data-testid={`add-cart-${i}`}>
                  <Plus className="w-4 h-4" strokeWidth={2} /> Add
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
